import { AdvisoryLink } from './AdvisoryLink';
import { MissionFeatureCollection } from '@autopylot-internal/tiles-client';
import { ITFRAdvisoryProperties } from '@autopylot-internal/tiles-client';
import { SecondaryWarningAdvisory } from '../SecondaryAdvisory';
import { formatLocalDateAndTime } from '../../support/helpers/format';
import { NoWrap } from '../../components/typography/NoWrap';
import { ICompositeAdvisoryProperties } from './ICompositeAdvisoryProperties';

const TFRLabel = (props: ITFRAdvisoryProperties) => {
    const startedBeforeToday = props.effectiveStart < new Date().toISOString();
    return <strong>{startedBeforeToday ? 'TFR' : 'Scheduled TFR'}</strong>;
};

const TFRLink = (props: ITFRAdvisoryProperties) => {
    const tfrUrl = `https://tfr.faa.gov/tfr3/?page=detail_${props.number.replace('/', '_')}.html`;
    return <strong><AdvisoryLink text={props.number} url={tfrUrl} /></strong>;
};

const TFRActiveRange = (props: ITFRAdvisoryProperties & { mission: MissionFeatureCollection }) => {
    const startedBeforeToday = props.effectiveStart < new Date().toISOString();

    const end = props.effectiveEnd === 'PERM' ? undefined : formatLocalDateAndTime({ time: props.effectiveEnd, location: props.mission.getMissionCenter(), noYear: true });
    const start = formatLocalDateAndTime({ time: props.effectiveStart, location: props.mission.getMissionCenter(), noYear: true });

    if (end) {
        if (startedBeforeToday) {
            return <span>active <strong>through <NoWrap>{end}</NoWrap></strong></span>;
        } else {
            return <span>active <strong><NoWrap>{start}</NoWrap> to <NoWrap>{end}</NoWrap></strong></span>;
        }
    } else {
        if (startedBeforeToday) {
            return <span>active <strong>permanently</strong></span>;
        } else {
            return <span>active <strong>from <NoWrap>{start}</NoWrap> permanently</strong></span>;
        }
    }
};

export const CompositeTFRAdvisory = (props: ICompositeAdvisoryProperties<ITFRAdvisoryProperties> & { mission: MissionFeatureCollection } ) => {
    const tfrs = props.advisories;
    const missionProps = props.mission.getMissionRadius().properties;

    const isActiveDuringMission = (tfr: ITFRAdvisoryProperties) => tfr.effectiveStart < missionProps.endTime && (tfr.effectiveEnd === 'PERM' || tfr.effectiveEnd > missionProps.startTime);
    const blockTFRs = tfrs.filter(tfr => isActiveDuringMission(tfr) && tfr.advisoryType === 'block');
    const adviseTFRs = tfrs.filter(tfr => isActiveDuringMission(tfr) && tfr.advisoryType !== 'block');
    const outsideTFRs = tfrs.filter(tfr => !isActiveDuringMission(tfr));

    const singleTFR = tfrs.length === 1;
    const any = (_tfrs: ITFRAdvisoryProperties[]) => _tfrs.length > 0;

    return (
        <SecondaryWarningAdvisory header="Temporary Flight Restrictions" advisoryType={any(blockTFRs) ? 'block' : 'advise'} GLOBAL_ID={tfrs[0].GLOBAL_ID}>
            {!any(blockTFRs) &&
                <p>
                    Your mission is not restricted by any TFRs for this timeframe. For a list of all active and upcoming TFRs, visit <AdvisoryLink url="https://tfr.faa.gov" />.
                </p>
            }

            {any(blockTFRs) && singleTFR &&
                <p>Your mission is affected by <TFRLabel {...blockTFRs[0]} /> <TFRLink {...blockTFRs[0]} />, <TFRActiveRange {...blockTFRs[0]} mission={props.mission} />.</p>
            }
            {any(blockTFRs) && !singleTFR &&
                <p>
                    Your mission is affected by {blockTFRs.length > 1 ? 'these TFRs' : 'this TFR'}:
                    <ul>
                        {blockTFRs.map(tfr => (
                            <li key={tfr.number}><TFRLink {...tfr} />: <TFRActiveRange {...tfr} mission={props.mission} /></li>
                        ))}
                    </ul>
                </p>
            }

            {any(adviseTFRs) && singleTFR &&
                <p>Be advised, your mission may be affected by <TFRLabel {...adviseTFRs[0]} /> <TFRLink {...adviseTFRs[0]} />, <TFRActiveRange {...adviseTFRs[0]} mission={props.mission} />.</p>
            }
            {any(adviseTFRs) && !singleTFR &&
                <p>
                    Be advised, mission may be affected by {adviseTFRs.length > 1 ? 'these TFRs' : 'this TFR'}:
                    <ul>
                        {adviseTFRs.map(tfr => (
                            <li key={tfr.number}><TFRLink {...tfr} />: <TFRActiveRange {...tfr} mission={props.mission} /></li>
                        ))}
                    </ul>
                </p>
            }

            {any(outsideTFRs) && singleTFR &&
                <p>Your mission area overlaps <TFRLabel {...outsideTFRs[0]} /> <TFRLink {...outsideTFRs[0]} />, <TFRActiveRange {...outsideTFRs[0]} mission={props.mission} />, outside mission timeframe.</p>
            }
            {any(outsideTFRs) && !singleTFR &&
                <p>
                    Mission area overlaps TFR{outsideTFRs.length > 1 ? 's' : ''} outside mission timeframe:
                    <ul>
                        {outsideTFRs.map(tfr => (
                            <li key={tfr.number}><TFRLink {...tfr} />: <TFRActiveRange {...tfr} mission={props.mission} /></li>
                        ))}
                    </ul>
                </p>
            }

            {any(blockTFRs) &&
                <>
                    <p>
                        No person may operate a small unmanned aircraft in restricted airspace
                        unless that person has permission from the appropriate authority.
                    </p>
                    <p>
                        Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.
                    </p>
                </>
            }
        </SecondaryWarningAdvisory>
    );
};
